/**
 * Thin typed web API client for the PitchBook Company Targets feature (web/v2).
 *
 * Types are defined locally because the PitchBook endpoints were added after
 * the last contracts:codegen run. Migrate to @nu-assist/contracts imports once
 * the next codegen pass runs and exports the PitchBook schemas.
 *
 * Auth: makeAuthedFetch (single-flight 401 → POST /auth/refresh → retry-once),
 * same lifecycle as ImportsClient. All requests use credentials: "include".
 */

import { makeAuthedFetch } from "@nu-assist/shared-fe";
import type { FetchLike } from "./imports.client";

// ---------------------------------------------------------------------------
// Local type definitions (mirror packages/api/app/web/v2/pitchbook/schemas.py)
// ---------------------------------------------------------------------------

export interface PitchBookUploadCreated {
  import_id: string;
  upload_url: string;
  expires_in: number;
}

export interface PitchBookAcceptedJob {
  job_id: string;
  poll_url: string;
}

export interface PitchBookUploadStatus {
  import_id: string;
  status: "uploaded" | "parsing" | "ready" | "failed";
  parsed_rows: number | null;
  error_message: string | null;
}

export interface PitchBookCompanyItem {
  company_id: string;
  name: string | null;
  pitchbook_id: string | null;
  url: string | null;
  sector: string | null;
  hq_city: string | null;
  hq_country: string | null;
  founded_year: number | null;
  description: string | null;
  raw_pitchbook: Record<string, unknown> | null;
}

export interface PitchBookCompanyListResponse {
  items: PitchBookCompanyItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface ScrapedCompanyInput {
  name: string;
  pitchbook_id?: string | null;
  url?: string | null;
  sector?: string | null;
  hq_city?: string | null;
  hq_country?: string | null;
  founded_year?: number | null;
  description?: string | null;
  raw_pitchbook?: Record<string, unknown>;
}

export interface ScrapeCompaniesResult {
  added: number;
  updated: number;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export class PitchBookApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
  ) {
    super(`PitchBook API request failed (${status}) for ${url}`);
    this.name = "PitchBookApiError";
    Object.setPrototypeOf(this, PitchBookApiError.prototype);
  }
}

export interface PitchBookClientConfig {
  apiBase: string;
  fetchImpl?: FetchLike;
}

export interface ListCompaniesParams {
  page?: number;
  page_size?: number;
  sort_key?: string;
  sort_dir?: "asc" | "desc";
}

function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createPitchBookClient(cfg: PitchBookClientConfig) {
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${cfg.apiBase}${path}`;
    const res = await authedFetch(url, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "content-type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) throw new PitchBookApiError(res.status, url);
    return (await res.json()) as T;
  }

  async function requestVoid(method: string, path: string): Promise<void> {
    const url = `${cfg.apiBase}${path}`;
    const res = await authedFetch(url, { method, credentials: "include" });
    if (!res.ok) throw new PitchBookApiError(res.status, url);
  }

  const base = (searchId: string) =>
    `/api/web/v2/searches/${encodeURIComponent(searchId)}/company-list`;

  return {
    /** POST — create presigned S3 PUT + insert import row. Every upload
     *  creates one company list; `name` names it (omitted = the filename) and
     *  `source` says whether it came from Chopbook or a web CSV import. */
    createUpload(
      searchId: string,
      filename: string,
      opts: { name?: string; source?: "chopbook" | "csv_upload" } = {},
    ): Promise<PitchBookUploadCreated> {
      return request<PitchBookUploadCreated>(
        "POST",
        `${base(searchId)}/upload${buildQuery({ filename, name: opts.name, source: opts.source })}`,
      );
    },

    /** POST — verify S3 object exists then enqueue parse job. */
    completeUpload(searchId: string, importId: string): Promise<PitchBookAcceptedJob> {
      return request<PitchBookAcceptedJob>(
        "POST",
        `${base(searchId)}/upload/${encodeURIComponent(importId)}/complete`,
      );
    },

    /** GET — poll parse job status. */
    getUploadStatus(searchId: string, importId: string): Promise<PitchBookUploadStatus> {
      return request<PitchBookUploadStatus>(
        "GET",
        `${base(searchId)}/upload/${encodeURIComponent(importId)}/status`,
      );
    },

    /** GET — paginated company list for a search. */
    listCompanies(
      searchId: string,
      params: ListCompaniesParams = {},
    ): Promise<PitchBookCompanyListResponse> {
      return request<PitchBookCompanyListResponse>(
        "GET",
        `${base(searchId)}${buildQuery(params)}`,
      );
    },

    /** DELETE — remove a company from the target list. 204 no-body. */
    removeCompany(searchId: string, companyId: string): Promise<void> {
      return requestVoid("DELETE", `${base(searchId)}/${encodeURIComponent(companyId)}`);
    },

    /** POST — sync upsert of browser-scraped companies (no SQS). */
    scrapeCompanies(
      searchId: string,
      companies: ScrapedCompanyInput[],
    ): Promise<ScrapeCompaniesResult> {
      return request<ScrapeCompaniesResult>("POST", `${base(searchId)}/scrape`, {
        companies,
      });
    },
  };
}

export type PitchBookClient = ReturnType<typeof createPitchBookClient>;
